import React, { useState, useEffect } from "react";
import { 
    Card, Row, Col, Button, Spinner, Alert, Table,
    Form, Badge, Pagination, OverlayTrigger, Tooltip,
    ProgressBar, Modal
} from "react-bootstrap";
import { Link, useNavigate } from "react-router-dom";
import { formatCurrency, formatDate, formatNumber } from "../utils/formatters";
import Footer from "../layouts/Footer";
import Header from "../layouts/Header";

export default function EncoursClients() {
    const navigate = useNavigate();
    const [resume, setResume] = useState(null);
    const [encours, setEncours] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [filterText, setFilterText] = useState('');
    const [filterRetard, setFilterRetard] = useState('tous');
    const [itemsPerPage, setItemsPerPage] = useState(10);
    const [currentPage, setCurrentPage] = useState(1);
    const [showDetailModal, setShowDetailModal] = useState(false);
    const [clientDetail, setClientDetail] = useState(null);
    const [facturesClient, setFacturesClient] = useState([]);
    const [loadingDetail, setLoadingDetail] = useState(false);

    useEffect(() => {
        fetchDonnees();
    }, [filterRetard]);

    const fetchDonnees = async () => {
        setLoading(true);
        try {
            const token = localStorage.getItem("token");
            
            // Récupérer le résumé
            const resumeResponse = await fetch(
                'https://sawd.julesferry-ga.com/api/finances.php?action=resume_encours',
                { headers: { 'Authorization': `Bearer ${token}` } }
            );
            
            // Récupérer la liste des encours
            let url = 'https://sawd.julesferry-ga.com/api/finances.php?action=encours_clients';
            const params = new URLSearchParams();
            if (filterText) params.append('recherche', filterText);
            if (filterRetard !== 'tous') params.append('retard', filterRetard);
            if (params.toString()) url += '&' + params.toString();
            
            const encoursResponse = await fetch(url, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            
            if (!resumeResponse.ok || !encoursResponse.ok) {
                throw new Error("Erreur chargement des données");
            }
            
            const resumeResult = await resumeResponse.json();
            const encoursResult = await encoursResponse.json();
            
            setResume(resumeResult.data);
            setEncours(encoursResult.data || []);
            setCurrentPage(1);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const fetchDetailClient = async (idClient) => {
        setLoadingDetail(true);
        try {
            const token = localStorage.getItem("token");
            const response = await fetch(
                `https://sawd.julesferry-ga.com/api/finances.php?action=detail_encours&id_client=${idClient}`,
                { headers: { 'Authorization': `Bearer ${token}` } }
            );
            
            if (!response.ok) throw new Error("Erreur chargement");
            
            const result = await response.json();
            setFacturesClient(result.data || []);
            setShowDetailModal(true);
        } catch (err) {
            alert("Erreur: " + err.message);
        } finally {
            setLoadingDetail(false);
        }
    };

    // Pagination
    const indexOfLastItem = currentPage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
    const currentItems = encours.slice(indexOfFirstItem, indexOfLastItem);
    const totalPages = Math.ceil(encours.length / itemsPerPage);

    const handlePageChange = (pageNumber) => {
        setCurrentPage(pageNumber);
        document.querySelector('.table-card')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    };

    const getRetourBadge = (jours) => {
        if (jours < 0) {
            return <Badge bg="success" className="rounded-pill px-3 py-2">À venir</Badge>;
        } else if (jours <= 30) {
            return <Badge bg="warning" className="rounded-pill px-3 py-2">{jours}j</Badge>;
        } else if (jours <= 60) {
            return <Badge bg="danger" className="rounded-pill px-3 py-2">{jours}j</Badge>;
        } else {
            return <Badge bg="danger" className="rounded-pill px-3 py-2">+60j</Badge>;
        }
    };

    if (loading) {
        return (
            <>
                <Header />
                <div className="main main-app p-3 p-lg-4">
                    <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '60vh' }}>
                        <div className="text-center">
                            <Spinner animation="border" variant="primary" className="mb-3" style={{ width: '3rem', height: '3rem' }} />
                            <p className="text-muted">Chargement des encours clients...</p>
                        </div>
                    </div>
                </div>
            </>
        );
    }

    if (error) {
        return (
            <>
                <Header />
                <div className="main main-app p-3 p-lg-4">
                    <Alert variant="danger" className="text-center py-5">
                        <i className="ri-error-warning-line fs-48 mb-3 d-block"></i>
                        <h4>{error}</h4>
                        <Button variant="outline-danger" className="mt-3" onClick={fetchDonnees}>
                            <i className="ri-refresh-line me-2"></i>Réessayer
                        </Button>
                    </Alert>
                </div>
            </>
        );
    }

    return (
        <>
            <Header />
            <div className="main main-app p-3 p-lg-4">
                <div className="container-fluid">
                    {/* En-tête */}
                    <Row className="mb-4 fade-in">
                        <Col md={12}>
                            <div className="d-flex justify-content-between align-items-center">
                                <div className="d-flex align-items-center gap-3">
                                    <div 
                                        className="page-icon-wrapper d-flex align-items-center justify-content-center" 
                                        style={{ 
                                            width: '70px', 
                                            height: '70px', 
                                            borderRadius: '20px',
                                            background: 'linear-gradient(135deg, rgba(239, 68, 68, 0.15), rgba(220, 38, 38, 0.15))',
                                            boxShadow: '0 10px 20px rgba(239, 68, 68, 0.15)',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            flexShrink: 0
                                        }}
                                    >
                                        <i className="ri-bank-card-line text-danger" style={{ fontSize: '36px', lineHeight: 1 }}></i>
                                    </div>
                                    <div>
                                        <h1 className="page-title mb-1">Encours clients</h1>
                                        <p className="text-muted mb-0">Suivi des créances clients</p>
                                    </div>
                                </div>
                                <Button variant="outline-secondary" onClick={() => navigate('/stats/dashboard')}>
                                    <i className="ri-arrow-left-line me-2"></i>Retour
                                </Button>
                            </div>
                        </Col>
                    </Row>

                    {/* Cartes de résumé */}
                    {resume && (
                        <Row className="g-3 mb-4 stats-row">
                            <Col md={4}>
                                <Card className="border-0 shadow-sm h-100 stat-card">
                                    <Card.Body className="p-3 d-flex align-items-center">
                                        <div className="stat-icon-wrapper bg-danger-soft me-3">
                                            <i className="ri-money-euro-circle-line text-danger fs-4"></i>
                                        </div>
                                        <div>
                                            <small className="text-muted d-block text-uppercase fw-semibold tracking-wide">
                                                Total créances
                                            </small>
                                            <h3 className="mb-0 fw-bold text-danger">{formatCurrency(resume.total?.total_creances || 0)}</h3>
                                            <small className="text-muted">{resume.total?.nb_factures_impayees || 0} factures</small>
                                        </div>
                                    </Card.Body>
                                </Card>
                            </Col>
                            <Col md={4}>
                                <Card className="border-0 shadow-sm h-100 stat-card">
                                    <Card.Body className="p-3 d-flex align-items-center">
                                        <div className="stat-icon-wrapper bg-warning-soft me-3">
                                            <i className="ri-group-line text-warning fs-4"></i>
                                        </div>
                                        <div>
                                            <small className="text-muted d-block text-uppercase fw-semibold tracking-wide">
                                                Clients débiteurs
                                            </small>
                                            <h3 className="mb-0 fw-bold text-warning">{resume.total?.nb_clients_debiteurs || 0}</h3>
                                            <small className="text-muted">avec encours</small>
                                        </div>
                                    </Card.Body>
                                </Card>
                            </Col>
                            <Col md={4}>
                                <Card className="border-0 shadow-sm h-100 stat-card">
                                    <Card.Body className="p-3 d-flex align-items-center">
                                        <div className="stat-icon-wrapper bg-info-soft me-3">
                                            <i className="ri-calculator-line text-info fs-4"></i>
                                        </div>
                                        <div>
                                            <small className="text-muted d-block text-uppercase fw-semibold tracking-wide">
                                                Encours moyen
                                            </small>
                                            <h3 className="mb-0 fw-bold text-info">
                                                {formatCurrency((resume.total?.total_creances || 0) / (resume.total?.nb_clients_debiteurs || 1))}
                                            </h3>
                                            <small className="text-muted">par client</small>
                                        </div>
                                    </Card.Body>
                                </Card>
                            </Col>
                        </Row>
                    )}

                    {/* Répartition par ancienneté */}
                    {resume && resume.anciennete && (
                        <Card className="border-0 shadow-sm mb-4">
                            <Card.Header className="bg-white py-3">
                                <h5 className="mb-0">
                                    <i className="ri-timer-line me-2 text-primary"></i>
                                    Répartition par ancienneté
                                </h5>
                            </Card.Header>
                            <Card.Body>
                                <Row className="g-3">
                                    <Col md={3}>
                                        <div className="p-3 bg-light rounded-3 text-center">
                                            <small className="text-muted">À venir</small>
                                            <h5 className="mb-0 text-success">{formatCurrency(resume.anciennete.a_venir || 0)}</h5>
                                        </div>
                                    </Col>
                                    <Col md={3}>
                                        <div className="p-3 bg-light rounded-3 text-center">
                                            <small className="text-muted">1-30 jours</small>
                                            <h5 className="mb-0 text-warning">{formatCurrency(resume.anciennete.retard_30j || 0)}</h5>
                                        </div>
                                    </Col>
                                    <Col md={3}>
                                        <div className="p-3 bg-light rounded-3 text-center">
                                            <small className="text-muted">31-60 jours</small>
                                            <h5 className="mb-0 text-danger">{formatCurrency(resume.anciennete.retard_60j || 0)}</h5>
                                        </div>
                                    </Col>
                                    <Col md={3}>
                                        <div className="p-3 bg-light rounded-3 text-center">
                                            <small className="text-muted">+60 jours</small>
                                            <h5 className="mb-0 text-danger">{formatCurrency(resume.anciennete.retard_plus_60j || 0)}</h5>
                                        </div>
                                    </Col>
                                </Row>
                            </Card.Body>
                        </Card>
                    )}

                    {/* Filtres */}
                    <Card className="filter-card border-0 shadow-sm mb-4">
                        <Card.Body>
                            <Row className="g-3 align-items-end">
                                <Col md={4}>
                                    <Form.Group>
                                        <Form.Label className="fw-semibold text-muted small text-uppercase">
                                            <i className="ri-search-line me-2"></i>
                                            Recherche
                                        </Form.Label>
                                        <Form.Control
                                            type="text"
                                            placeholder="Client, code..."
                                            value={filterText}
                                            onChange={(e) => setFilterText(e.target.value)}
                                            className="filter-select py-2"
                                        />
                                    </Form.Group>
                                </Col>
                                <Col md={3}>
                                    <Form.Group>
                                        <Form.Label className="fw-semibold text-muted small text-uppercase">
                                            <i className="ri-filter-line me-2"></i>
                                            Filtre retard
                                        </Form.Label>
                                        <Form.Select 
                                            value={filterRetard} 
                                            onChange={(e) => setFilterRetard(e.target.value)}
                                            className="filter-select py-2"
                                        >
                                            <option value="tous">Tous</option>
                                            <option value="a_venir">À venir</option>
                                            <option value="retard_30">1-30 jours</option>
                                            <option value="retard_60">31-60 jours</option>
                                            <option value="retard_plus">+60 jours</option>
                                        </Form.Select>
                                    </Form.Group>
                                </Col>
                                <Col md={3}>
                                    <Form.Group>
                                        <Form.Label className="fw-semibold text-muted small text-uppercase">
                                            <i className="ri-list-settings-line me-2"></i>
                                            Affichage
                                        </Form.Label>
                                        <Form.Select 
                                            value={itemsPerPage} 
                                            onChange={(e) => {
                                                setItemsPerPage(parseInt(e.target.value));
                                                setCurrentPage(1);
                                            }}
                                            className="filter-select py-2"
                                        >
                                            <option value="5">5 par page</option>
                                            <option value="10">10 par page</option>
                                            <option value="20">20 par page</option>
                                            <option value="50">50 par page</option>
                                        </Form.Select>
                                    </Form.Group>
                                </Col>
                                <Col md={2}>
                                    <Button 
                                        variant="primary" 
                                        onClick={fetchDonnees}
                                        className="w-100 py-2"
                                        style={{
                                            background: 'linear-gradient(135deg, #4361ee, #3a56d4)',
                                            border: 'none',
                                            borderRadius: '10px'
                                        }}
                                    >
                                        <i className="ri-refresh-line"></i>
                                    </Button>
                                </Col>
                            </Row>
                        </Card.Body>
                    </Card>

                    {/* Tableau des encours */}
                    <Card className="border-0 shadow-sm table-card">
                        <Card.Header className="bg-white py-3 d-flex justify-content-between align-items-center">
                            <h5 className="mb-0">
                                <i className="ri-group-line me-2 text-danger"></i>
                                Détail des encours
                            </h5>
                            <Badge bg="danger" className="px-3 py-2">
                                {encours.length} clients
                            </Badge>
                        </Card.Header>
                        <Card.Body className="p-0">
                            <div className="table-responsive">
                                <Table hover className="mb-0 align-middle custom-table">
                                    <thead className="bg-light">
                                        <tr>
                                            <th>Client</th>
                                            <th className="text-center">Factures</th>
                                            <th className="text-end">Encours total</th>
                                            <th>Dernière facture</th>
                                            <th>Échéance</th>
                                            <th className="text-center">Retard</th>
                                            <th className="text-center">Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {currentItems.map((client, index) => {
                                            const joursRetard = client.jours_retard_max || 0;
                                            return (
                                                <tr key={client.id_client}>
                                                    <td>
                                                        <div className="d-flex align-items-center">
                                                            <div className="bg-light rounded-circle d-flex align-items-center justify-content-center me-2" 
                                                                 style={{ width: '40px', height: '40px' }}>
                                                                <i className="ri-user-3-line text-danger"></i>
                                                            </div>
                                                            <div>
                                                                <Link to={`/ventes/clients/${client.id_client}`} className="text-primary fw-semibold text-decoration-none">
                                                                    {client.raison_sociale}
                                                                </Link>
                                                                <div>
                                                                    <small className="text-muted">{client.code_client}</small>
                                                                    {client.telephone && (
                                                                        <small className="text-muted ms-2">
                                                                            <i className="ri-phone-line me-1"></i>
                                                                            {client.telephone}
                                                                        </small>
                                                                    )}
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </td>
                                                    <td className="text-center">
                                                        <Badge bg="info" className="rounded-pill px-3 py-2">
                                                            {client.nb_factures}
                                                        </Badge>
                                                    </td>
                                                    <td className="text-end fw-bold text-danger">
                                                        {formatCurrency(client.total_encours)}
                                                    </td>
                                                    <td>
                                                        <span className="text-muted">
                                                            <i className="ri-calendar-line me-1"></i>
                                                            {formatDate(client.derniere_facture)}
                                                        </span>
                                                    </td>
                                                    <td>
                                                        <span className="text-muted">
                                                            <i className="ri-calendar-check-line me-1"></i>
                                                            {formatDate(client.derniere_echeance)}
                                                        </span>
                                                    </td>
                                                    <td className="text-center">
                                                        {getRetourBadge(joursRetard)}
                                                    </td>
                                                    <td className="text-center">
                                                        <OverlayTrigger placement="top" overlay={<Tooltip>Voir le détail</Tooltip>}>
                                                            <Button 
                                                                variant="outline-info" 
                                                                size="sm"
                                                                className="rounded-circle me-1"
                                                                style={{ width: '36px', height: '36px' }}
                                                                onClick={() => {
                                                                    setClientDetail(client);
                                                                    fetchDetailClient(client.id_client);
                                                                }}
                                                            >
                                                                <i className="ri-eye-line"></i>
                                                            </Button>
                                                        </OverlayTrigger>
                                                        <OverlayTrigger placement="top" overlay={<Tooltip>Saisir un règlement</Tooltip>}>
                                                            <Button 
                                                                variant="outline-success" 
                                                                size="sm"
                                                                className="rounded-circle"
                                                                style={{ width: '36px', height: '36px' }}
                                                                onClick={() => navigate(`/ventes/NouveauReglement?client=${client.id_client}`)}
                                                            >
                                                                <i className="ri-bank-card-line"></i>
                                                            </Button>
                                                        </OverlayTrigger>
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </Table>
                            </div>
                        </Card.Body>
                        
                        {/* Pagination */}
                        {totalPages > 1 && (
                            <Card.Footer className="bg-white py-3">
                                <div className="d-flex justify-content-between align-items-center">
                                    <div className="text-muted small">
                                        Affichage de {indexOfFirstItem + 1} à {Math.min(indexOfLastItem, encours.length)} sur {encours.length} clients
                                    </div>
                                    
                                    <Pagination className="mb-0">
                                        <Pagination.First onClick={() => handlePageChange(1)} disabled={currentPage === 1} />
                                        <Pagination.Prev onClick={() => handlePageChange(currentPage - 1)} disabled={currentPage === 1} />
                                        
                                        {[...Array(Math.min(5, totalPages))].map((_, i) => {
                                            let pageNum;
                                            if (totalPages <= 5) pageNum = i + 1;
                                            else if (currentPage <= 3) pageNum = i + 1;
                                            else if (currentPage >= totalPages - 2) pageNum = totalPages - 4 + i;
                                            else pageNum = currentPage - 2 + i;
                                            
                                            return (
                                                <Pagination.Item key={i} active={pageNum === currentPage} onClick={() => handlePageChange(pageNum)}>
                                                    {pageNum}
                                                </Pagination.Item>
                                            );
                                        })}
                                        
                                        <Pagination.Next onClick={() => handlePageChange(currentPage + 1)} disabled={currentPage === totalPages} />
                                        <Pagination.Last onClick={() => handlePageChange(totalPages)} disabled={currentPage === totalPages} />
                                    </Pagination>
                                </div>
                            </Card.Footer>
                        )}
                    </Card>

                    {/* Modal de détail */}
                    <Modal show={showDetailModal} onHide={() => setShowDetailModal(false)} size="lg" centered>
                        <Modal.Header closeButton>
                            <Modal.Title>
                                <i className="ri-file-list-line me-2 text-primary"></i>
                                Factures impayées - {clientDetail?.raison_sociale}
                            </Modal.Title>
                        </Modal.Header>
                        <Modal.Body>
                            {loadingDetail ? (
                                <div className="text-center py-4">
                                    <Spinner animation="border" variant="primary" />
                                </div>
                            ) : facturesClient.length === 0 ? (
                                <p className="text-muted text-center py-4">Aucune facture impayée</p>
                            ) : (
                                <div className="table-responsive">
                                    <Table hover className="mb-0">
                                        <thead>
                                            <tr>
                                                <th>Facture</th>
                                                <th>Date</th>
                                                <th>Échéance</th>
                                                <th className="text-end">Montant</th>
                                                <th className="text-end">Réglé</th>
                                                <th className="text-end">Solde</th>
                                                <th className="text-center">Retard</th>
                                                <th className="text-center">Action</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {facturesClient.map(f => (
                                                <tr key={f.id_facture}>
                                                    <td>
                                                        <Link to={`/ventes/facturesDetails/${f.id_facture}`} className="text-primary">
                                                            {f.numero_facture}
                                                        </Link>
                                                    </td>
                                                    <td>{formatDate(f.date_facture)}</td>
                                                    <td className={f.jours_retard > 0 ? 'text-danger fw-bold' : ''}>
                                                        {formatDate(f.date_echeance)}
                                                    </td>
                                                    <td className="text-end">{formatCurrency(f.montant_ttc)}</td>
                                                    <td className="text-end text-success">{formatCurrency(f.montant_regle)}</td>
                                                    <td className="text-end fw-bold text-danger">{formatCurrency(f.solde)}</td>
                                                    <td className="text-center">
                                                        {f.jours_retard > 0 ? (
                                                            <Badge bg="danger" className="rounded-pill px-3 py-2">
                                                                {f.jours_retard}j
                                                            </Badge>
                                                        ) : (
                                                            <Badge bg="success" className="rounded-pill px-3 py-2">
                                                                OK
                                                            </Badge>
                                                        )}
                                                    </td>
                                                    <td className="text-center">
                                                        <Button 
                                                            variant="outline-success" 
                                                            size="sm"
                                                            className="rounded-circle"
                                                            style={{ width: '32px', height: '32px' }}
                                                            onClick={() => navigate(`/ventes/NouveauReglement?facture=${f.id_facture}&client=${clientDetail.id_client}`)}
                                                        >
                                                            <i className="ri-bank-card-line"></i>
                                                        </Button>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </Table>
                                </div>
                            )}
                        </Modal.Body>
                        <Modal.Footer>
                            <Button variant="secondary" onClick={() => setShowDetailModal(false)}>
                                Fermer
                            </Button>
                            <Button 
                                variant="primary" 
                                onClick={() => navigate(`/ventes/NouveauReglement?client=${clientDetail?.id_client}`)}
                            >
                                <i className="ri-bank-card-line me-2"></i>
                                Saisir un règlement
                            </Button>
                        </Modal.Footer>
                    </Modal>
                </div>

                <style>{`
                    @keyframes fadeIn { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: translateY(0); } }
                    .fade-in { animation: fadeIn 0.5s ease; }
                    .stats-row .stat-card { transition: all 0.3s ease; border-radius: 16px; }
                    .stats-row .stat-card:hover { transform: translateY(-5px); box-shadow: 0 20px 25px -5px rgba(0,0,0,0.1) !important; }
                    .stat-icon-wrapper { width: 56px; height: 56px; display: flex; align-items: center; justify-content: center; border-radius: 16px; transition: transform 0.3s ease; }
                    .stat-card:hover .stat-icon-wrapper { transform: scale(1.1); }
                    .filter-card { border-radius: 16px; background: #f8fafc; }
                    .filter-select { border-radius: 10px; border: 1px solid #e2e8f0; transition: all 0.2s ease; }
                    .filter-select:focus { border-color: #4361ee; box-shadow: 0 0 0 3px rgba(67, 97, 238, 0.1); }
                    .table-card { border-radius: 16px; overflow: hidden; }
                    .custom-table thead th { background: #f8fafc; color: #1e293b; font-weight: 600; font-size: 0.85rem; text-transform: uppercase; letter-spacing: 0.5px; border-bottom: 2px solid #e2e8f0; padding: 1rem 0.75rem; }
                    .custom-table tbody td { padding: 1rem 0.75rem; vertical-align: middle; }
                    .custom-table tbody tr:hover { background: #f1f5f9 !important; }
                    .bg-danger-soft { background: rgba(239, 68, 68, 0.1); }
                    .bg-warning-soft { background: rgba(245, 158, 11, 0.1); }
                    .bg-info-soft { background: rgba(13, 202, 240, 0.1); }
                    .tracking-wide { letter-spacing: 0.5px; }
                    .pagination .page-link { border-radius: 8px; margin: 0 2px; color: #4361ee; border: 1px solid #e2e8f0; }
                    .pagination .page-link:hover { background: #f1f5f9; color: #3a56d4; border-color: #cbd5e1; }
                    .pagination .active .page-link { background: linear-gradient(135deg, #4361ee, #3a56d4); border-color: #4361ee; color: white; }
                `}</style>

                <Footer />
            </div>
        </>
    );
}